import React from 'react'
import { Box } from '@mui/material'

export type GlassContainerVariant = 'main' | 'card' | 'showcase' | 'pill' | 'circle'
export type GlassContainerSize = 'small' | 'medium' | 'large' | 'xl'

export interface GlassContainerProps {
  children: React.ReactNode
  variant?: GlassContainerVariant
  size?: GlassContainerSize
  className?: string
  style?: React.CSSProperties
  onClick?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void

  // Glass effect customization
  blur?: number
  opacity?: number
  borderOpacity?: number
  shadowIntensity?: number

  // Advanced effects
  noise?: boolean
  shimmer?: boolean
  liquid?: boolean
}

/**
 * GlassContainer Component
 *
 * An advanced glassmorphism container with customizable effects
 * Based on liquid-glass.pro generator and modern glassmorphism design
 *
 * @param children - Content to be rendered inside the container
 * @param variant - Visual style variant
 * @param size - Size of the container
 * @param className - Additional CSS classes
 * @param style - Custom inline styles
 * @param onClick - Click handler
 * @param onMouseEnter - Mouse enter handler
 * @param onMouseLeave - Mouse leave handler
 * @param blur - Backdrop blur intensity (0-50)
 * @param opacity - Background opacity (0-1)
 * @param borderOpacity - Border opacity (0-1)
 * @param shadowIntensity - Shadow intensity (0-1)
 * @param noise - Enable noise texture
 * @param shimmer - Enable shimmer effect
 * @param liquid - Enable liquid glass effect
 */
export const GlassContainer: React.FC<GlassContainerProps> = ({
  children,
  variant = 'card',
  size = 'medium',
  className = '',
  style = {},
  onClick,
  onMouseEnter,
  onMouseLeave,
  blur = 20,
  opacity = 0.1,
  borderOpacity = 0.2,
  shadowIntensity = 0.3,
  noise = false,
  shimmer = false,
  liquid = false
}) => {
  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return { padding: '12px', minHeight: '80px' }
      case 'large':
        return { padding: '32px', minHeight: '200px' }
      case 'xl':
        return { padding: '40px', minHeight: '300px' }
      case 'medium':
      default:
        return { padding: '20px', minHeight: '120px' }
    }
  }

  // Get variant styles
  const getVariantStyles = () => {
    const baseStyles = {
      borderRadius: '16px',
      background: `linear-gradient(135deg, rgba(255, 255, 255, ${opacity}), rgba(255, 255, 255, ${opacity * 0.5}))`,
      backdropFilter: `blur(${blur}px)`,
      border: `1px solid rgba(255, 255, 255, ${borderOpacity})`,
      boxShadow: `0 8px 32px rgba(0, 0, 0, ${shadowIntensity}), inset 0 1px 0 rgba(255, 255, 255, ${borderOpacity})`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative' as const,
      overflow: 'hidden' as const
    }

    switch (variant) {
      case 'main':
        return {
          ...baseStyles,
          borderRadius: '24px',
          background: `linear-gradient(135deg, rgba(255, 255, 255, ${opacity * 1.5}), rgba(255, 255, 255, ${opacity}))`,
          backdropFilter: `blur(${blur + 5}px)`,
          boxShadow: `0 12px 40px rgba(0, 0, 0, ${shadowIntensity + 0.1}), inset 0 1px 0 rgba(255, 255, 255, ${borderOpacity + 0.1})`
        }
      case 'card':
        return {
          ...baseStyles,
          borderRadius: '20px'
        }
      case 'showcase':
        return {
          ...baseStyles,
          borderRadius: '16px',
          background: `linear-gradient(135deg, rgba(255, 255, 255, ${opacity * 1.2}), rgba(255, 255, 255, ${opacity * 0.6}))`,
          backdropFilter: `blur(${blur + 10}px)`
        }
      case 'pill':
        return {
          ...baseStyles,
          borderRadius: '50px',
          padding: '12px 24px',
          minHeight: 'auto',
          background: `linear-gradient(135deg, rgba(255, 255, 255, ${opacity * 1.3}), rgba(255, 255, 255, ${opacity * 0.7}))`
        }
      case 'circle':
        return {
          ...baseStyles,
          borderRadius: '50%',
          aspectRatio: '1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, rgba(255, 255, 255, ${opacity * 1.4}), rgba(255, 255, 255, ${opacity * 0.8}))`
        }
      default:
        return baseStyles
    }
  }

  const sizeClasses = getSizeClasses()
  const variantStyles = getVariantStyles()

  // Generate noise texture if enabled
  const noiseStyle = noise
    ? {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.1'/%3E%3C/svg%3E\")",
          pointerEvents: 'none' as const,
          zIndex: 1
        }
      }
    : {}

  // Generate shimmer effect if enabled
  const shimmerStyle = shimmer
    ? {
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
          animation: 'liquid-shimmer 3s ease-in-out infinite',
          pointerEvents: 'none' as const,
          zIndex: 1
        }
      }
    : {}

  // Generate liquid effect if enabled
  const liquidStyle = liquid
    ? {
        '&:hover': {
          background: `linear-gradient(135deg, rgba(255, 255, 255, ${opacity * 2}), rgba(255, 255, 255, ${opacity}))`,
          backdropFilter: `blur(${blur + 15}px)`,
          boxShadow: `0 20px 60px rgba(0, 0, 0, ${shadowIntensity + 0.3}), inset 0 1px 0 rgba(255, 255, 255, ${borderOpacity + 0.3})`
        }
      }
    : {}

  return (
    <>
      <Box
        sx={{
          ...variantStyles,
          ...sizeClasses,
          ...noiseStyle,
          ...shimmerStyle,
          ...liquidStyle,
          cursor: onClick ? 'pointer' : 'default'
        }}
        style={style}
        className={className}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>{children}</Box>
      </Box>

      {/* Add CSS animations for shimmer effect */}
      {shimmer && (
        <style>{`
          @keyframes liquid-shimmer {
            0%, 100% { transform: translateX(-100%); }
            50% { transform: translateX(100%); }
          }
        `}</style>
      )}
    </>
  )
}

export default GlassContainer
